import type { ReactNode } from 'react';
import { useAuth } from '@/providers';
import { EmailVerificationRequired } from './EmailVerificationRequired';

interface EmailVerificationGuardProps {
    children: ReactNode;
    requireVerification?: boolean;
}

/**
 * EmailVerificationGuard component checks if the user's email is verified
 * and shows a verification required screen if not verified
 */
export function EmailVerificationGuard({ 
    children, 
    requireVerification = true 
}: EmailVerificationGuardProps) {
    const { user, isAuthenticated, isLoading } = useAuth();

    // Show loading state while checking authentication
    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    // If not authenticated, render children (auth guards should handle this)
    if (!isAuthenticated || !user) {
        return <>{children}</>;
    }

    // If verification is required but user email is not verified
    if (requireVerification && !user.emailVerified) {
        return <EmailVerificationRequired />;
    }

    // Email is verified or verification not required, render children
    return <>{children}</>;
}
